import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserEditDialog({ open, onOpenChange, user, roles, branches, onSaved }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        role: user.role || '',
        department: user.department || '',
        designation: user.designation || '',
        branch_id: user.branch_id || '',
        is_active: user.is_active !== false,
        monthly_target: user.monthly_target ?? '',
        badge: user.badge || 'none',
        password: '',
      });
    }
  }, [user]);

  if (!user) return null;

  const save = async () => {
    try {
      // Only send changed / meaningful fields (skip empty password)
      const payload = {
        name: form.name,
        email: form.email || null,
        role: form.role,
        department: form.department || null,
        designation: form.designation || null,
        branch_id: form.branch_id || null,
        is_active: !!form.is_active,
        monthly_target: form.monthly_target === '' ? 0 : Number(form.monthly_target) || 0,
        badge: !form.badge || form.badge === 'none' ? null : form.badge,
      };
      if (form.password && form.password.trim().length >= 4) {
        payload.password = form.password;
      }
      await axios.put(`${API_URL}/api/users/update/${user.id}`, payload, { withCredentials: true });
      toast.success('User updated');
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle>Edit User — {user.username}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Update name, role, branch, permissions status and other details. Leave password blank to keep it unchanged.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Full Name *</Label>
            <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="edit-user-name" className="bg-slate-800 border-slate-700 text-white" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Email</Label>
            <Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="edit-user-email" className="bg-slate-800 border-slate-700 text-white" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Role *</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="edit-user-role">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white max-h-60">
                {(roles || []).map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Branch</Label>
            <Select value={form.branch_id || 'none'}
              onValueChange={(v) => setForm({ ...form, branch_id: v === 'none' ? '' : v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="edit-user-branch">
                <SelectValue placeholder="No branch (HQ)" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white max-h-60">
                <SelectItem value="none">No branch (HQ)</SelectItem>
                {(branches || []).map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Department</Label>
            <Input value={form.department || ''} onChange={(e) => setForm({ ...form, department: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Designation</Label>
            <Input value={form.designation || ''} onChange={(e) => setForm({ ...form, designation: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Monthly Customer Target</Label>
            <Input type="number" min="0" value={form.monthly_target ?? ''}
              onChange={(e) => setForm({ ...form, monthly_target: e.target.value })}
              placeholder="e.g. 15"
              data-testid="edit-user-monthly-target"
              className="bg-slate-800 border-slate-700 text-white" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Gamification Badge</Label>
            <Select value={form.badge || 'none'} onValueChange={(v) => setForm({ ...form, badge: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="edit-user-badge">
                <SelectValue placeholder="No badge" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white">
                <SelectItem value="none">No badge</SelectItem>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="bronze">Bronze</SelectItem>
                <SelectItem value="gold">Gold</SelectItem>
                <SelectItem value="platinum">Platinum</SelectItem>
                <SelectItem value="diamond">Diamond</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 col-span-2">
            <Label className="text-slate-300">Reset Password (min 4 chars — leave blank to keep unchanged)</Label>
            <Input type="password" value={form.password || ''}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="edit-user-password"
              className="bg-slate-800 border-slate-700 text-white" />
          </div>
          <div className="flex items-center justify-between col-span-2 border-t border-slate-800 pt-3">
            <Label className="text-slate-300">Active</Label>
            <Switch checked={!!form.is_active}
              onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              data-testid="edit-user-active" />
          </div>
        </div>
        <div className="flex justify-end space-x-2 border-t border-slate-800 pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">Cancel</Button>
          <Button onClick={save} data-testid="submit-edit-user" className="bg-amber-500 hover:bg-amber-600">Save Changes</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
